import React, { useMemo, useState } from "react";
import CommonCollapsible from "../../../../components/common/CommonCollapsible";
import { LuClock, LuPlane } from "react-icons/lu";
import {
  IoMdArrowDropdown,
  IoMdArrowDropup,
  IoMdCalendar,
} from "react-icons/io";
import { flightDirectionDuration } from "../../../../controllers/flightController";
import { changeOrderRouteChange } from "../../../../controllers/ticketManagement/changeDateControllers";

function ChangeDateConfirmationFlightDetails({ data }) {
  const [openIndex, setOpenIndex] = useState();
  const selectedOffer = data?.selectedOffer;

  const routeChange = changeOrderRouteChange(
    data?.flightBooking,
    data?.selectedOffer,
  );
  const isManual = !data?.selectedOffer && !routeChange;

  const newDirections = useMemo(() => {
    if (routeChange)
      return data?.selectedOffer?.directions?.map((direction) =>
        direction?.map((segment) => ({
          ...segment,
          airline: segment?.airline?.marketing,
          airlineImage: segment?.airline?.image?.url,
          airlineName: segment?.airline?.image?.description,
          arrivalAirport: segment?.arrival?.airport,
          arrivalDate: segment?.arrival?.date,
          arrivalLocation: segment?.arrival?.location,
          arrivalLocationName: segment?.arrival?.city,
          arrivalTerminal: segment?.arrival?.terminal,
          arrivalTime: segment?.arrival?.time,
          departureAirport: segment?.departure?.airport,
          departureDate: segment?.departure?.date,
          departureLocation: segment?.departure?.location,
          departureLocationName: segment?.departure?.city,
          departureTerminal: segment?.departure?.terminal,
          departureTime: segment?.departure?.time,
        })),
      );
    if (data?.changedFlightBooking?.directions?.length)
      return data?.changedFlightBooking?.directions;

    const directionLib = {};
    const selectedDirections = selectedOffer?.directions || [];
    for (const direction of selectedDirections) {
      const firstSeg = direction[0];
      const lastSeg = direction[direction?.length - 1];
      directionLib[
        `${firstSeg?.departure?.location}-${lastSeg?.arrival?.location}`
      ] = direction?.map((item) => ({ ...item, changed: true }));
    }
    const oldDirections = data?.flightBooking?.flights;

    const groupDirections = () => {
      const results = {};
      for (const segment of oldDirections || []) {
        const direction = results[segment?.segment];
        if (direction) {
          results[segment?.segment] = [...direction, segment];
        } else {
          results[segment?.segment] = [segment];
        }
      }
      return Object.values(results);
    };
    const updatedDirections = groupDirections().map((direction) => {
      const firstSeg = direction[0];
      const lastSeg = direction[direction?.length - 1];
      const route = `${firstSeg?.departureLocation}-${lastSeg?.arrivalLocation}`;
      const changedDirection = directionLib[route];
      if (changedDirection) return changedDirection;
      return direction;
    });
    return updatedDirections || [];
  }, [data]);

  const newDirectionLib = useMemo(() => {
    const lib = {};
    for (const segment of data?.newDirections || []) {
      const existing = lib[Number(segment?.segment)];
      if (existing) continue;
      lib[Number(segment?.segment)] = segment;
    }
    return lib;
  }, [data]);

  return (
    <div className="w-full space-y-4 mt-4">
      <div className="flex items-center gap-2">
        <span className="font-bold text-xl"> Flight Details</span>
      </div>

      {newDirections.map((direction, i) => (
        <div
          key={i}
          className="bg-white rounded-xl shadow-sm p-4 flex flex-col w-full"
        >
          <ChangeDetailsHeaderRow
            index={i}
            onClick={() =>
              i === openIndex ? setOpenIndex(null) : setOpenIndex(i)
            }
            direction={direction}
            open={i === openIndex}
            routeChange={routeChange}
            requestedDate={
              isManual ? newDirectionLib[i + 1]?.departure : undefined
            }
          />

          <CommonCollapsible open={openIndex === i} duration={200}>
            <div className="flex w-full py-3">
              <div className="pt-3 flex flex-col gap-3 bg-[#B6CDF9]/10 w-full p-3">
                {direction.map((seg, j) => (
                  <div className="w-full flex flex-col" key={j}>
                    <div className="w-full hidden lg:flex text-sm items-center font-medium text-gray-800 mb-1">
                      <span className="">{seg?.flightNumber}</span>
                      <hr className="w-6 mx-1 h-px border-b border-gray-300" />
                      <span className="w-32 text-start mr-2">
                        {seg?.airlineName || seg?.airline?.image?.description}
                      </span>
                      <span>{seg?.cabinClass}</span>
                      <span className="bg-gray-200 rounded-md px-1 text-sm ml-2">
                        {seg?.bookingClass}
                      </span>
                      <div className="flex items-center gap-1 text-sm text-gray-600 ml-2">
                        <LuClock className="text-gray-500" />
                        <span>{seg?.duration}</span>
                      </div>
                      <div className="text-gray-500 text-sm ml-4">
                        Operated by{" "}
                        <span className="text-black">
                          {seg?.airlineName || seg?.airline?.image?.description}
                        </span>
                      </div>
                    </div>
                    <div className="w-full flex gap-1 flex-col lg:hidden text-sm items-center font-medium text-gray-800 mb-1">
                      <div className="flex w-full items-center justify-between">
                        <span className="">{seg?.flightNumber}</span>
                        <hr className="flex-1 mx-1 h-px border-b border-gray-300" />
                        <span className=" text-end mr-2">
                          {seg?.airlineName || seg?.airline?.image?.description}
                        </span>
                      </div>
                      <div className="flex w-full items-center">
                        <span>{seg?.cabinClass}</span>
                        <span className="bg-gray-200 rounded-md px-1 text-sm ml-2">
                          {seg?.bookingClass}
                        </span>
                        <div className="flex-1"></div>
                        <div className="flex items-center gap-1 text-sm text-gray-600 ml-2">
                          <LuClock className="text-gray-500" />
                          <span>{seg?.duration}</span>
                        </div>
                      </div>
                      <div className="text-gray-500 text-sm w-full">
                        Operated by{" "}
                        <span className="text-black">
                          {seg?.airlineName || seg?.airline?.image?.description}
                        </span>
                      </div>
                    </div>
                    <div className="flex lg:hidden mt-3"></div>
                    <div className="w-full hidden lg:flex text-sm items-center font-medium text-gray-800 mb-1">
                      <span className="w-20">
                        {seg?.departureTime || seg?.departure?.time}
                      </span>
                      <span className="w-32 text-gray-500">
                        {seg?.departureDate || seg?.departure?.date}
                      </span>
                      <span className="w-10 text-primary1">
                        {seg?.departureLocation || seg?.departure?.location}
                      </span>
                      <span className=" text-gray-500">
                        {seg?.departureAirport || seg?.departure?.airport}
                      </span>
                    </div>
                    <div className="w-full flex lg:hidden flex-col text-sm items-center font-medium text-gray-800 mb-1">
                      <div className="w-full flex">
                        <span className="w-20">
                          {seg?.departureTime || seg?.departure?.time}
                        </span>
                        <span className="w-32 text-gray-500">
                          {seg?.departureDate || seg?.departure?.date}
                        </span>
                        <span className="flex-1 text-primary1 text-end">
                          {seg?.departureLocation || seg?.departure?.location}
                        </span>
                      </div>
                      <span className="text-gray-500 text-end w-full">
                        {seg?.departureAirport || seg?.departure?.airport}
                      </span>
                    </div>
                    <div className="w-full hidden lg:flex text-sm items-center font-medium text-gray-800 mb-1">
                      <span className="w-20">
                        {seg?.arrivalTime || seg?.arrival?.time}
                      </span>
                      <span className="w-32 text-gray-500">
                        {seg?.arrivalDate || seg?.arrival?.date}
                      </span>
                      <span className="w-10 text-primary1">
                        {seg?.arrivalLocation || seg?.arrival?.location}
                      </span>
                      <span className=" text-gray-500">
                        {seg?.arrivalAirport || seg?.arrival?.airport}
                      </span>
                    </div>
                    <div className="w-full flex lg:hidden flex-col text-sm items-center font-medium text-gray-800 mb-1">
                      <div className="w-full flex">
                        <span className="w-20">
                          {seg?.arrivalTime || seg?.arrival?.time}
                        </span>
                        <span className="w-32 text-gray-500">
                          {seg?.arrivalDate || seg?.arrival?.date}
                        </span>
                        <span className="flex-1 text-primary1 text-end">
                          {seg?.arrivalLocation || seg?.arrival?.location}
                        </span>
                      </div>
                      <span className="text-gray-500 text-end w-full">
                        {seg?.arrivalAirport || seg?.arrival?.airport}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CommonCollapsible>
        </div>
      ))}
    </div>
  );
}

export default ChangeDateConfirmationFlightDetails;

function ChangeDetailsHeaderRow({
  index = 0,
  onClick,
  open,
  direction,
  routeChange,
  requestedDate,
}) {
  const firstSegemt = direction[0];
  const lastSegment = direction[direction?.length - 1];
  const logo = firstSegemt?.airlineImage || firstSegemt?.airline?.image?.url;
  const directionDuration = flightDirectionDuration(
    direction?.map((segment) => {
      if (!segment?.departure) {
        segment.departure = {
          time: segment?.departureTime,
          date: segment?.departureDate,
        };
      }
      if (!segment?.arrival) {
        segment.arrival = {
          time: segment?.arrivalTime,
          date: segment?.arrivalDate,
        };
      }
      return segment;
    }) || [],
  );

  return (
    <div
      className="w-full flex lg:items-center gap-3 flex-col lg:flex-row justify-between cursor-pointer"
      onClick={onClick}
    >
      {/* Left chunk: badge + airline + route + times */}
      <div className="flex lg:items-center gap-3 min-w-0 flex-col lg:flex-row">
        {/* index badge */}
        <div className="w-6 h-6  rounded-md bg-primary1 text-white text-xs font-semibold hidden lg:flex items-center justify-center">
          {index + 1}
        </div>

        {/* airline logo circle (placeholder) */}
        <img src={logo} alt="" className="w-12 lg:w-6 h-6 object-contain" />

        {/* route + time */}
        <div className="flex items-start flex-col min-w-0">
          {/* route */}
          <div className="text-sm text-gray-800 min-w-0">
            <span className="truncate">
              {firstSegemt?.departureLocationName ||
                firstSegemt?.departure?.city}{" "}
              <a className="text-primary1 hover:underline" href="#">
                {firstSegemt?.departureLocation ||
                  firstSegemt?.departure?.location}
              </a>{" "}
              —{" "}
              {lastSegment?.arrivalLocationName ||
                lastSegment?.arrival?.location}{" "}
              <a className="text-primary1 hover:underline" href="#">
                {lastSegment?.arrivalLocation ||
                  lastSegment?.arrivalLocationName}
              </a>
            </span>
          </div>
          <div className="flex lg:items-center gap-1 lg:gap-4 flex-col lg:flex-row">
            <div className="text-sm text-gray-900">
              <span className="font-semibold">
                {firstSegemt?.departureTime || firstSegemt?.departure?.time}
              </span>{" "}
              {firstSegemt?.departureDate || firstSegemt?.departure?.time} —{" "}
              <span className="font-semibold">
                {lastSegment?.arrivalTime || lastSegment?.arrival?.time}
              </span>{" "}
              {lastSegment?.arrivalDate || lastSegment?.arrival?.date}
            </div>

            {/* duration */}
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <LuClock className="text-gray-500" />
              <span>{directionDuration}</span>
            </div>
          </div>

          {/* times */}
        </div>
      </div>

      {/* Right chunk: Change button + caret */}
      {firstSegemt?.changed || routeChange || requestedDate ? (
        <div className="flex items-center gap-2 w-full lg:w-fit justify-end lg:justify-normal">
          {routeChange ? (
            <span className="rounded-md border border-primary1/40 bg-primary1/10 text-primary1 text-xs px-3 py-1">
              Re-Route
            </span>
          ) : requestedDate ? (
            <div className="flex gap-2">
              <span className="rounded-md border border-primary1/40 bg-primary1/10 text-primary1 text-xs px-3 py-1 flex gap-1 items-center">
                <IoMdCalendar />
                {requestedDate}
              </span>
              <span className="rounded-md border border-red-400 bg-red-200 text-red-500 text-xs px-3 py-1">
                Manual Change
              </span>
            </div>
          ) : (
            <span className="rounded-md border border-red-400 bg-red-200 text-red-500 text-xs px-3 py-1">
              Change
            </span>
          )}
          {open ? (
            <IoMdArrowDropup className="text-lg" />
          ) : (
            <IoMdArrowDropdown className="text-lg" />
          )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
